import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography } from "@mui/material";
import { FaCreditCard, FaWifi, FaCheck, FaTimes } from "react-icons/fa";
import { colors } from "../../Config/Colors";

export default function NFCInput(props) {
  const {
    name,
    label,
    value,
    error = null,
    onChange,
    disabled = false,
  } = props;

  const [isReading, setIsReading] = useState(false);
  const [nfcSupported, setNfcSupported] = useState(false);
  const [lastReadCard, setLastReadCard] = useState("");

  useEffect(() => {
    // NFC desteğini kontrol et
    // eslint-disable-next-line no-undef
    if ("NDEFReader" in window) {
      setNfcSupported(true);
    }
  }, []);

  const startNFCReading = async () => {
    if (!nfcSupported) {
      alert(
        "NFC okuma desteklenmiyor. Android Chrome tarayıcısı kullanın veya kart ID'sini manuel girin."
      );
      return;
    }

    try {
      setIsReading(true);
      // eslint-disable-next-line no-undef
      const ndef = new NDEFReader();

      await ndef.scan();

      ndef.addEventListener("reading", ({ message, serialNumber }) => {
        setLastReadCard(serialNumber);

        // Form değerini güncelle
        const event = {
          target: {
            name: name,
            value: serialNumber,
          },
        };
        onChange(event);

        setIsReading(false);
      });

      ndef.addEventListener("readingerror", () => {
        setIsReading(false);
        alert("NFC kart okunamadı. Lütfen tekrar deneyin.");
      });
    } catch (error) {
      setIsReading(false);
      alert("NFC okuma başlatılamadı: " + error.message);
    }
  };

  const stopNFCReading = () => {
    setIsReading(false);
  };

  return (
    <Box className="space-y-3">
      <TextField
        className="w-full"
        disabled={disabled}
        variant="outlined"
        label={label || undefined}
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder="Kart ID'sini manuel girin veya NFC ile okutun"
        {...(error && { error: true, helperText: error })}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: "#fff",
            fontSize: "15px",
            "& fieldset": {
              borderColor: "#d1d5db",
              borderWidth: "1px",
            },
            "&:hover fieldset": {
              borderColor: "#9ca3af",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#6366f1",
              borderWidth: "2px",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#6b7280",
            backgroundColor: "#fff",
            fontSize: "14px",
          },
          "& input": {
            padding: "12px 14px",
          },
        }}
        InputProps={{
          endAdornment: (
            <Box className="flex items-center gap-2">
              <FaCreditCard className="text-gray-400 w-5 h-5" />
            </Box>
          ),
        }}
      />

      {/* NFC Okuma Butonları - Sadece destekleniyorsa göster */}
      {nfcSupported && (
        <Box className="flex gap-3">
          {!isReading ? (
            <Button
              variant="contained"
              onClick={startNFCReading}
              disabled={disabled}
              className="flex items-center gap-2"
              sx={{
                backgroundColor: "#29A19C",
                "&:hover": {
                  backgroundColor: "#1e7a75",
                },
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "14px",
                padding: "8px 16px",
              }}
            >
              <FaWifi className="w-4 h-4" />
              NFC Oku
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={stopNFCReading}
              className="flex items-center gap-2"
              sx={{
                borderColor: "#f59e0b",
                color: "#f59e0b",
                "&:hover": {
                  borderColor: "#d97706",
                  backgroundColor: "#fef3c7",
                },
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "14px",
                padding: "8px 16px",
              }}
            >
              <FaTimes className="w-4 h-4" />
              Okumayı Durdur
            </Button>
          )}
        </Box>
      )}

      {/* Durum Mesajları */}
      {isReading && (
        <Box className="flex items-center gap-2 text-blue-600">
          <FaWifi className="w-4 h-4 animate-pulse" />
          <Typography variant="body2">
            NFC kartını cihaza yaklaştırın...
          </Typography>
        </Box>
      )}

      {!nfcSupported && (
        <Box className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
          <FaTimes className="w-4 h-4" />
          <div>
            <Typography variant="body2" className="font-medium">
              NFC okuma desteklenmiyor
            </Typography>
            <Typography variant="caption" className="text-amber-700">
              Android Chrome tarayıcısı kullanın veya kart ID'sini manuel girin
            </Typography>
          </div>
        </Box>
      )}

      {lastReadCard && lastReadCard === value && (
        <Box className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg border border-green-200">
          <FaCheck className="w-4 h-4" />
          <div>
            <Typography variant="body2" className="font-medium">
              Kart başarıyla okundu
            </Typography>
            <Typography variant="caption" className="text-green-700">
              ID: {lastReadCard}
            </Typography>
          </div>
        </Box>
      )}
    </Box>
  );
}
